

import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent,
  Typography,
  CircularProgress, 
  Snackbar, 
  Alert,
  Divider,
  Chip,IconButton
} from '@mui/material';
import { CheckCircle, ArrowBack } from '@mui/icons-material';
import { orderAssignmentAPI } from './deliveryAgentAPI';

const PickupConfirmation = ({ order, onBack, onPickupConfirmed }) => {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'info' 
  });

  const handleConfirmPickup = async () => {
    try {
      setLoading(true);
      
      const userId = sessionStorage.getItem('userId');
      if (!userId) {
        throw new Error('User ID not found in session');
      }
      
      if (!order.id) {
        throw new Error('Order ID is missing');
      }
      
      if (!order.orderType) {
        throw new Error('Order type is missing');
      }
      
      // CRITICAL: Check if assignment is accepted
      if (order.assignmentStatus !== 'accepted') {
        throw new Error(`Cannot pickup: Assignment must be accepted first. Current status: "${order.assignmentStatus}"`);
      }
      
      console.log('Sending pickup request:', {
        userId,
        orderId: order.id,
        orderType: order.orderType,
        assignmentStatus: order.assignmentStatus
      });
      
      // const response = await orderAssignmentAPI.confirmPickup(
      //   userId,
      //   order.id,
      //   order.orderType,
      //   {
      //     pickupTime: new Date().toISOString(),
      //     notes: ''
      //   },
      //   // token
      // );
      const response = await orderAssignmentAPI.confirmPickup(
        userId,
        order.id,
        order.orderType,
        {
          pickupTime: new Date().toISOString(),
          notes: '',
          pickupType: order.deliveryType || (order.orderType === 'booking' ? 'sample' : 'medicine') // ✅ ADD THIS
        }
      );
      
      console.log('Pickup response:', response.data);
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Pickup confirmed successfully!',
          severity: 'success'
        });
        
        // Wait before transitioning to next screen
        setTimeout(() => {
          onPickupConfirmed();
        }, 1500);
      } else {
        throw new Error(response.data.message || 'Pickup confirmation failed');
      }
      
    } catch (error) {
      console.error('Pickup Error:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = 'Failed to confirm pickup';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if we can proceed with pickup
  const canPickup = order.assignmentStatus === 'accepted' && 
                     order.deliveryStatus !== 'picked_up' && 
                     order.deliveryStatus !== 'delivered';

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <IconButton onClick={onBack} disabled={loading}>
          <ArrowBack />
        </IconButton>
      <Card elevation={3}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#006D77' }}>
            Confirm Pickup
          </Typography>
          
          <Divider sx={{ my: 2 }} />
          
          {/* Validation Warnings */}
          {order.assignmentStatus !== 'accepted' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>Cannot proceed:</strong> Assignment must be accepted before pickup.
              <br />
              Current status: <strong>{order.assignmentStatus}</strong>
            </Alert>
          )}

          {(order.deliveryStatus === 'picked_up' || order.deliveryStatus === 'delivered') && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This order has already been {order.deliveryStatus}
            </Alert>
          )}
          
          {/* Order Details */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Order ID:
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {order.orderId}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Customer:
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {order.customerName}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Phone:
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                <a href={`tel:${order.customerPhone}`} style={{ color: '#006D77', textDecoration: 'none' }}>
                  {order.customerPhone}
                </a>
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Type:
              </Typography>
              <Chip 
                label={order.orderType} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Assignment Status:
              </Typography>
              <Chip 
                label={order.assignmentStatus || 'unknown'} 
                size="small" 
                color={order.assignmentStatus === 'accepted' ? 'success' : 'warning'}
              />
            </Box>
            
            {order.deliveryStatus && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Delivery Status:
                </Typography>
                <Chip 
                  label={order.deliveryStatus} 
                  size="small" 
                  color={
                    order.deliveryStatus === 'delivered' ? 'success' :
                    order.deliveryStatus === 'picked_up' ? 'info' :
                    'default'
                  }
                />
              </Box>
            )}

            {order.totalAmount && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Amount:
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  ₹{order.totalAmount}
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* Customer Address */}
          {order.customerAddress && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Pickup Address:
              </Typography>
              <Typography variant="body2">
                {order.customerAddress.fullAddress}
              </Typography>
              {order.customerAddress.landmark && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Landmark: {order.customerAddress.landmark}
                </Typography>
              )}
            </Box>
          )}
          
          {/* Action Buttons */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>

            <Button 
              variant="contained" 
              onClick={handleConfirmPickup}
              disabled={loading || !canPickup}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
              fullWidth
              sx={{
                bgcolor: '#006D77',
                '&:hover': { bgcolor: '#005561' },
                '&:disabled': { bgcolor: '#ccc' }
              }}
            >
              {loading ? 'Confirming...' : 'Confirm Pickup'}
            </Button>
          </Box>
          
          {/* Help Text */}
          {!canPickup && order.assignmentStatus !== 'accepted' && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
              Please accept the assignment first from the order details page
            </Typography>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PickupConfirmation;